import * as React from 'react';
import { Link } from "react-router-dom";
import { useQuery } from '../../Components/Query';
import { Column, Table } from "../../Components/QueryTable";
import { Authorization, GlobalAuthorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { MatchSortHeader } from '../../Components/MatchSortHeader';
import { WebPortalClient, Study } from 'webportalclient';
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { LocalizedDateTime, DateTimeAccuracy } from '../../misc/components/LocalizedDateTime';
import Condition from '../../misc/components/Condition';
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import { DateMatchSortHeader } from '../../Components/DateMatchSortHeader';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { DeviceTypes } from '../../misc/api/apiTypes';
import { ExportSpirotracConfigurationModal } from './ExportSpirotracConfigurationModal';
import { uniqBy } from '../../misc/ArrayFunctions';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { StudyInformationModal } from './StudyInformationModal';

export const StudiesTable = () => {
    const { currentUserPermissions } = useSecurityContext();
    const { data, applyFilters } = useQuery();
    const client = useWebPortalApiClient();
    const [nameOptions, setNameOptions] = React.useState([]);
    const [vitalRefOptions, setVitalRefOptions] = React.useState([]);
    const [protocolOptions, setProtocolOptions] = React.useState([]);
    const [exportModalVisible, setExportModalVisible] = React.useState<boolean>(false);
    const [exportedStudyId, setExportedStudyId] = React.useState<string>();
    const [studyInfoModalVisible, setStudyInfoModalVisible] = React.useState<boolean>(false);
    const [studyInformationMessage, setStudyInformationMessage] = React.useState<string>("");


    React.useEffect(() => {

        const getFilterOptions = async () => {

            const studiesResult = await client.client.getStudies('name,protocol,vitalref');
            const nameOptions = uniqBy(studiesResult.value.map(s => ({
                value: s.name,
                label: s.name,
            })), (a, b) => a.value === b.value);

            setNameOptions(nameOptions);

            const protocolOptions = studiesResult.value.map(s => ({
                value: s.protocol,
                label: s.protocol,
            }));

            setProtocolOptions(protocolOptions);

            const vitalRefOptions = studiesResult.value.map(s => ({
                value: s.vitalRef,
                label: s.vitalRef,
            }));

            setVitalRefOptions(vitalRefOptions);
        };

        getFilterOptions();
    }, []);

    const handleClearFilters = () => applyFilters([]);

    const exportSP6Configurations = async (studyId) => {
        const result = await client.client.deviceConfiguration({ studyId: studyId, deviceType: DeviceTypes.Spirotrac6 })
        result.resource.filter(e => e.content != "remote").forEach(resource => {
            const resourceContent = atob(resource.text)
            downloadFile(new Blob([resourceContent], { type: 'application/xml' }), resource.type + '.xml');
        });

        result.resource.filter(e => e.content == "remote").forEach(resource => {
            const content = JSON.stringify(resource, null, 4);
            downloadFile(new Blob([content], { type: 'application/json' }), resource.type + '.json');
        });
    }

    const downloadFile = (content, name) => {
        const url = window.URL.createObjectURL(content);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
    }

    const getStudyInformationMessage = async(id) => {
        const study = await client.client.getStudy(id, "welcomeMessage");
        setStudyInformationMessage(study.welcomeMessage);
        setStudyInfoModalVisible(true);
    }

    const menuFormatter = (study: Study) => {
        return (
            <Authorization permissionIdsAny={[PermissionEnum.EditStudy, PermissionEnum.ViewPopulationGroups, PermissionEnum.CustomiseStudy, PermissionEnum.EditStudyInformation]}
                studyId={study.id}
                currentPermissions={currentUserPermissions}>
                <div className="text-center">
                <div className="btn-group" role="group">
                    <button type="button" className="btn btn-primary btn-xs dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" data-automationid={`options_${study.id}`}>
                        <FontAwesomeIcon aria-hidden="true" icon={faBars} />
                    </button>
                    <ul className="dropdown-menu pull-right scrollable-menu" role="menu">
                        <Authorization
                            permissionIdsAny={[PermissionEnum.EditStudy]}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <Link to={`/studies/edit/${study.id}`} data-automationid={`edit-study_${study.id}`}>Edit</Link>
                            </li>
                        </Authorization>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.EditStudy]}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <Link data-automationid={`study-visit-schedule_${study.id}`} to={`/visitschedule/${study.id}`}>Visit Schedule</Link>
                            </li>
                        </Authorization>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.EditStudy]}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <Link data-automationid={`study-visit-schedule_${study.id}`} to={`/studies/settings/${study.id}`}>Settings</Link>
                            </li>
                        </Authorization>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.EditStudy]}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.Cough) !== undefined}>
                            <li>
                                <Link data-automationid={`study-cough-settings_${study.id}`} to={`/studies/coughsettings/${study.id}`}>Cough Events Settings</Link>
                            </li>
                            </Condition>
                        </Authorization>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.EditStudy]}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <Condition test={() => study && study.deviceTypeIds.find(id => id === DeviceTypes.Spirotrac6) !== undefined}>
                            <li>
                                <Link to="#" onClick={() => { setExportedStudyId(study.id); setExportModalVisible(true); }} data-automationid={`export-study-sp6-configuration_${study.id}`}>Export SP6 Configurations</Link>
                            </li>
                            </Condition>
                        </Authorization>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.ViewPopulationGroups]}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <Link to={`/studies/populationgroups/${study.id}`} data-automationid={`study-population-groups_${study.id}`}>Population Groups</Link>
                            </li>
                        </Authorization>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.CustomiseStudy]}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <Link to={`/studies/customisation/${study.id}`} data-automationid={`customise-study_${study.id}`}>Customise</Link>
                            </li>
                        </Authorization>
                        <Authorization
                             permissionIdsAny={[PermissionEnum.EditStudyInformation]}
                             studyId={study.id}
                             currentPermissions={currentUserPermissions}
                        >
                             <li>
                                <Link to="#" onClick={() => {
                                        setExportedStudyId(study.id);
                                        getStudyInformationMessage(study.id);
                                    }} data-automationid={`edit-study-information_${study.id}`}>Edit Study Information</Link>
                             </li>
                            </Authorization>
                    </ul>
                </div>
                </div>
            </Authorization>
        );
    };

    return (
        <ErrorBoundary>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Studies</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <GlobalAuthorization currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.CreateStudy]}>
                            <Link id="createNew" className="btn btn-primary" to="/studies/create">
                                Create New
                            </Link>&nbsp;
                        </GlobalAuthorization>
                        <button className="btn btn-primary" onClick={handleClearFilters}>Clear All Filters</button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div id="StudiesTable" className="table-responsive" data-automationid="StudiesTable">
                                <Table data={data}>
                                    <Column field="name" automationId="name" header={<MatchSortHeader title="Name" field="name" automationId="name" options={nameOptions} sortField="name" />} formatter={n => n} />
                                    <Column field="protocol" automationId="protocol" header={<MatchSortHeader title="Protocol Number" field="protocol" automationId="protocol" options={protocolOptions} sortField="protocol" />} formatter={n => n} />
                                    <Column field="vitalRef" automationId="vitalRef" header={<MatchSortHeader title="VitalRef" field="vitalRef" automationId="vitalRef" options={vitalRefOptions} sortField="vitalRef" />} formatter={n => n} />
                                    <Column field="modified" automationId="modified" header={<DateMatchSortHeader title="Modified At" field="modified" automationId="modified" sortField="modified" />} formatter={n => <LocalizedDateTime time={n} accuracy={DateTimeAccuracy.Second} isTooltip={true} />} />
                                    <Column automationId="hamburger-menu" field="" formatter={menuFormatter} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {exportModalVisible ? <ExportSpirotracConfigurationModal studyId={exportedStudyId} onClose={() => setExportModalVisible(false)} /> : <></>}
            {studyInfoModalVisible ? <StudyInformationModal studyId={exportedStudyId} initialValue={studyInformationMessage} onClose={() => setStudyInfoModalVisible(false)} /> : <></>}
        </ErrorBoundary>
    );
};
